/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import {
    ChatWithRequestPane,
    isRequestArchived,
    RequestActionType,
    RequestType,
    RequestTypeDisplayTextMap,
    UserCell,
} from '@strata/react-chat';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import {
    FormTitleLabelValuePair,
    HeaderLabel,
    HeaderLabelValuePair,
} from '@strata/react-label-value-pairs';
import { Icon } from '@strata/react-status-icons';
import { PayerRequestStatusIndicator } from '@strata/react-status-indicators';
import { SuccessToast } from '@strata/react-toast-message';
import { saveAs } from 'file-saver';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { getTreatmentsForPatient } from 'api/apertureAPI';
import { getRequestAttachment } from 'api/payerRequestAPI';
import { UpdateAssignmentDialog } from 'components/requests/dialogs/UpdateAssignmentDialog';
import { PayerRequestNumber } from 'components/treatments/PayerRequestNumber';
import { Permissions, SearchParamKeys } from 'services/constants';
import { handleError } from 'services/Error';
import { useGoBack } from 'services/navigationHelpers';
import { getActionButtonPageTitleLabel } from 'services/payerRequests';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { usePayerUserPbmAccessMap, useUserNameMap } from 'store/api/authApi';
import {
    type RequestDetails,
    useGetRequestDetailsQuery,
    usePostAttachFileMutation,
    usePostMarkRequestReadMutation,
    usePostRequestActivityMutation,
} from 'store/api/payerRequestApi';
import { useGetAllProductServicesQuery, useGetSegmentsQuery } from 'store/api/referenceApi';
import { useGetMemberDetailsQuery } from 'store/api/scopeAssessorApi';

const ActionDialogs = {
    UPDATE_ASSIGNMENT: 'UPDATE_ASSIGNMENT',
};
type ActionDialog = (typeof ActionDialogs)[keyof typeof ActionDialogs];

const ActionDialogComponentMap = {
    [ActionDialogs.UPDATE_ASSIGNMENT]: UpdateAssignmentDialog,
};

const RequestTypeWritePermissionMap = {
    [RequestType.ESCALATION]: Permissions.WRITE_PAYER_REQUEST_ESCALATION,
    [RequestType.CHANGE]: Permissions.WRITE_PAYER_REQUEST_CHANGE,
    [RequestType.OTHER]: Permissions.WRITE_PAYER_REQUEST_OTHER,
};

const mapActivityWithAuthorParty = (
    action: RequestDetails['activity'][number],
    userNameMap: Record<string, string>,
    pbmAccessByUpn: Record<string, boolean>,
) => {
    const fmhAction =
        action.fmhAction || (action.actorUpn ?? '').toLowerCase().includes('@freemarkethealth.com');
    return {
        ...action,
        actorFullName: userNameMap[action.actorUpn ?? ''],
        fmhAction,
        pbmAction: !fmhAction && !!action.actorUpn && !!pbmAccessByUpn[action.actorUpn],
        actionDetail:
            action.actionType === RequestActionType.ASSIGN_CL
                ? action.actionDetail && userNameMap[action.actionDetail]
                : action.actionDetail,
    };
};

// eslint-disable-next-line complexity
export const RequestDetailsPage = () => {
    const { showPayerRequestSubSubTypes } = useFlags<{
        showPayerRequestSubSubTypes: boolean;
    }>();
    const { requestId } = useParams() as { requestId: string };
    const { user } = useAuth0();
    const navigate = useNavigate();
    const { payerId, permissions } = useAppSelector((state) => state.auth);

    const { data, isFetching } = useGetRequestDetailsQuery({ payerId: payerId!, requestId });
    const [postRequestActivity] = usePostRequestActivityMutation();
    const [postAttachFile] = usePostAttachFileMutation();
    const userNameMap = useUserNameMap({ payerId: payerId! });
    const pbmAccessByUpn = usePayerUserPbmAccessMap(payerId);

    const { data: products } = useGetAllProductServicesQuery();
    const [treatmentProductOidMap, setTreatmentProductOidMap] = useState<Record<string, string>>(
        {},
    );

    useEffect(() => {
        void (async () => {
            try {
                if (!isFetching && data?.memberId) {
                    const result = await getTreatmentsForPatient({
                        memberId: data?.memberId,
                        memberFirstName: data?.firstName,
                        memberLastName: data?.lastName,
                        memberDob: data?.dateOfBirth,
                    });
                    setTreatmentProductOidMap(
                        Object.fromEntries(
                            (result?.treatments ?? []).map((treatment) => [
                                treatment.treatment_id,
                                treatment.product_service_oid,
                            ]),
                        ),
                    );
                }
            } catch (err) {
                handleError('An error occurred retrieving treatment details.', err as Error);
            }
        })();
    }, [data, isFetching]);

    const requestDetails = useMemo(
        () =>
            data
                ? {
                      ...data,
                      submitterFullName: userNameMap[data.submitterUpn],
                      payerAssignedFullName:
                          data.payerAssignedUpn && userNameMap[data.payerAssignedUpn],
                      activity: data.activity?.map((action) =>
                          mapActivityWithAuthorParty(action, userNameMap, pbmAccessByUpn),
                      ),
                      treatments: data.treatmentIds?.map((treatmentId) => ({
                          // We're appending the payerId as a query param here to handle scenarios
                          // where the user's default payer context is different than the current context.
                          // When a new tab is opened, we save the new URL in session storage under LOGIN_REDIRECT_KEY
                          // in the RequireAuth component.
                          // This url is then pulled into the AuthCallback logic and the payerId is parsed out
                          // for being used to fetch the new authorization token.
                          // The treatmentId property in this map is only used as a map key and for the hyperlink to
                          // the treatment in the RequestDetailsPane component in the react-chat Strata package.
                          treatmentId: `${treatmentId}?${SearchParamKeys.PAYER_ID}=${payerId}`,
                          productServiceDescription:
                              products?.productServiceMap[treatmentProductOidMap[treatmentId]],
                      })),
                  }
                : undefined,
        [
            data,
            userNameMap,
            pbmAccessByUpn,
            payerId,
            products?.productServiceMap,
            treatmentProductOidMap,
        ],
    );

    const { data: memberDetails, isFetching: isMemberDetailsFetching } = useGetMemberDetailsQuery(
        payerId && requestDetails?.requestType === RequestType.ESCALATION
            ? {
                  payerId,
                  segmentId: requestDetails.segmentId,
                  memberId: requestDetails.memberId!,
                  firstName: requestDetails.firstName!,
                  lastName: requestDetails.lastName!,
                  dateOfBirth: requestDetails.dateOfBirth!,
              }
            : skipToken,
    );

    // Once data loads, mark request as read
    const [postMarkRequestRead] = usePostMarkRequestReadMutation();
    useEffect(() => {
        void (async () => {
            if (!isFetching && requestId === data?.requestId) {
                await postMarkRequestRead({ payerId: payerId!, requestId });
            }
        })();
    }, [isFetching, data?.requestId, payerId, requestId, postMarkRequestRead]);

    const { data: segments } = useGetSegmentsQuery({ payerId: payerId! });

    const [actionDialogToShow, setActionDialogToShow] = useState<ActionDialog>();
    const ActiveActionDialog = ActionDialogComponentMap[actionDialogToShow ?? ''];
    const closeActionDialog = () => setActionDialogToShow(undefined);

    const requestLoaded = !isFetching && !!requestDetails;

    const canWriteRequestType =
        !!requestDetails?.requestType &&
        permissions.includes(RequestTypeWritePermissionMap[requestDetails.requestType]);

    const menuItems = useMemo(() => {
        const items: ComponentProps<typeof ButtonWithDropdownMenu>['menuItems'] = [];

        // No actions available without write permissions
        if (isFetching || !canWriteRequestType) {
            return items;
        }

        const addActionDialogMenuItem = (actionDialog: ActionDialog) => {
            items.push({
                text: ActionDialogComponentMap[actionDialog].Title,
                onItemClick: () => setActionDialogToShow(actionDialog),
            });
        };

        addActionDialogMenuItem(ActionDialogs.UPDATE_ASSIGNMENT);

        return items;
    }, [isFetching, canWriteRequestType]);

    const { backToDestination, goBack } = useGoBack(
        requestDetails
            ? {
                  route: {
                      pathname: AppRoutes.requests,
                      hash: isRequestArchived(requestDetails)
                          ? RequestType.ARCHIVE.toLocaleLowerCase()
                          : requestDetails.requestType?.toLocaleLowerCase(),
                  },
                  text: getActionButtonPageTitleLabel(requestDetails),
              }
            : {
                  route: AppRoutes.requests,
                  text: 'Requests',
              },
    );

    useEffect(() => {
        if (!isFetching && !requestDetails) {
            navigate(AppRoutes.requests);
        }
    }, [isFetching, navigate, requestDetails]);

    return (
        <FormHeaderAndContent
            rowOneContentHeader={
                requestLoaded ? (
                    <>
                        {ActiveActionDialog && (
                            <ActiveActionDialog
                                payerId={payerId!}
                                requestDetails={requestDetails}
                                onClose={closeActionDialog}
                            />
                        )}
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            id="request-details-back-button"
                            title={`${RequestTypeDisplayTextMap[requestDetails.requestType]} Details`}
                            onClick={() => goBack()}
                        />
                        {menuItems.length > 0 && (
                            <div style={{ marginLeft: 'auto' }}>
                                <ButtonWithDropdownMenu
                                    menuItems={menuItems}
                                    intent="primary"
                                    label="Actions"
                                />
                            </div>
                        )}
                    </>
                ) : undefined
            }
            rowTwoContentHeader={
                requestLoaded ? (
                    <>
                        {requestDetails.requestType === RequestType.ESCALATION && (
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    requestDetails.lastName,
                                    requestDetails.firstName,
                                )}
                                value={requestDetails.memberId}
                            />
                        )}
                        {requestDetails.requestType !== RequestType.OTHER && (
                            <HeaderLabelValuePair
                                label="Request Type"
                                value={requestDetails.requestSubType}
                            />
                        )}
                        {showPayerRequestSubSubTypes &&
                            requestDetails.requestType === RequestType.ESCALATION && (
                                <HeaderLabelValuePair
                                    label="Request Subtype"
                                    value={requestDetails.typeLevel2}
                                />
                            )}
                        {requestDetails.requestType !== RequestType.ESCALATION && (
                            <HeaderLabelValuePair label="Summary" value={requestDetails.summary} />
                        )}
                        <HeaderLabelValuePair
                            label="Created On"
                            value={formatDate(requestDetails.createDate, DateFormats.DATE_TIME)}
                        />
                        <div>
                            <HeaderLabel label="Created By" />
                            <UserCell
                                fullName={requestDetails.submitterFullName}
                                upn={requestDetails.submitterUpn}
                            />
                        </div>
                        <HeaderLabelValuePair
                            label="Request Urgency"
                            value={
                                requestDetails.urgent ? (
                                    <div
                                        className="flex__gap--x-small"
                                        style={{ alignItems: 'center' }}
                                    >
                                        <Icon
                                            icon={IconNames.WARNING_SIGN}
                                            intent={Icon.Intent.WARNING}
                                        />
                                        <span>Urgent</span>
                                    </div>
                                ) : undefined
                            }
                        />
                        <HeaderLabelValuePair
                            label="Request #"
                            showLabelTooltip={false}
                            showValueTooltip={false}
                            value={
                                <PayerRequestNumber
                                    text={requestDetails.requestNumber}
                                    pbmOnly={requestDetails.pbmOnly}
                                />
                            }
                        />
                        <HeaderLabelValuePair
                            label="Payer Assignee"
                            value={requestDetails.payerAssignedFullName}
                        />
                        <HeaderLabelValuePair
                            label="Business Segment"
                            value={segments?.segmentsMap?.[requestDetails.segmentId]}
                        />
                        <div>
                            <HeaderLabel label="Status" />
                            <PayerRequestStatusIndicator
                                status={
                                    requestDetails.requestStatus ===
                                    PayerRequestStatusIndicator.Statuses.RESOLVED
                                        ? PayerRequestStatusIndicator.Statuses.MONITORING
                                        : requestDetails.requestStatus
                                }
                            />
                        </div>
                    </>
                ) : undefined
            }
            rowThreeContentHeader={
                requestLoaded &&
                !isMemberDetailsFetching &&
                requestDetails.requestType === RequestType.ESCALATION &&
                memberDetails?.benefitGroup == null && (
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label="This member is unverified."
                    />
                )
            }
            className="tabbed-grid-content"
            mainContent={
                <ChatWithRequestPane
                    requestId={requestId}
                    request={requestDetails}
                    treatmentUrlBase={AppRoutes.treatmentDetails('')}
                    openTreatmentNewTab={false}
                    requestUrlBase={AppRoutes.requestDetails('')}
                    openPrevRequestNewTab={false}
                    onFileDownloadClick={async ({ requestAttachmentId, filename }) => {
                        try {
                            const response = await getRequestAttachment({
                                payerId: payerId!,
                                requestId,
                                requestAttachmentId,
                            });
                            saveAs(response, filename);
                        } catch (err) {
                            handleError('An error occurred downloading attachment.', err as Error);
                        }
                    }}
                    conversationType={ChatWithRequestPane.ConversationType.CHAT}
                    disabled={
                        !requestDetails?.requestType ||
                        !permissions.includes(
                            RequestTypeWritePermissionMap[requestDetails.requestType],
                        )
                    }
                    isLoading={isFetching}
                    userUPN={user?.email}
                    isSupportUser={false}
                    onMessageSend={({ details }) => {
                        void postRequestActivity({
                            payerId: payerId!,
                            requestId,
                            actionType: RequestActionType.MESSAGE,
                            actionDetail: details.message,
                        }).unwrap();
                    }}
                    onAddFile={async ({ file }) => {
                        await postAttachFile({
                            payerId: payerId!,
                            requestId,
                            attachment: file,
                        }).unwrap();
                        SuccessToast({ message: 'File added successfully.' });
                    }}
                    onApprovalComplete={({ actionTaken, actionDetail }) =>
                        postRequestActivity({
                            payerId: payerId!,
                            requestId,
                            actionType: actionTaken,
                            actionDetail,
                        }).unwrap()
                    }
                />
            }
        />
    );
};
